import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useLocation } from "react-router-dom";
import { TERMS_OF_SERVICE_MARKDOWN } from "../../legal/termsOfServiceText";
import PatientMeshDpaModal from "./PatientMeshDpaModal";
import { openDpaInNewTab } from "../../utils/openDpaWindow";
import authService from "../../services/authService";
import { clearDoctorCache } from "../../services/doctorService";
import { emitDoctorInfoUpdated } from "../../utils/doctorInfoEmitter";
import { isDoctorSessionActive, isTenantSessionActive } from "../../utils/authSession";

const CURRENT_TERMS_VERSION = "4 Eylül 2026";

export default function TermsConsentGuard({ children }) {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dpaOpen, setDpaOpen] = useState(false);
  const location = useLocation();

  const checkConsentStatus = useCallback(() => {
    // SuperAdmin veya genel kamu sayfalarındaysa atla
    const pathname = location.pathname || "";
    if (
      pathname.startsWith("/super-admin") ||
      pathname === "/" ||
      pathname === "/individual-login" ||
      pathname === "/corporate-login" ||
      pathname === "/about" ||
      pathname === "/contact" ||
      pathname.startsWith("/gizlilik-ve-guvenlik") ||
      pathname.startsWith("/forgot-password") ||
      pathname.startsWith("/verify-otp") ||
      pathname.startsWith("/reset-password") ||
      pathname.startsWith("/verify-2fa") ||
      pathname.startsWith("/tenantRegister") ||
      pathname.startsWith("/register-individual")
    ) {
      setOpen(false);
      return;
    }

    const doctorSession = isDoctorSessionActive();
    const tenantSession = isTenantSessionActive();

    if (!doctorSession && !tenantSession) {
      setOpen(false);
      return;
    }

    // Doctor veya Tenant kullanıcı bilgilerini oku
    let isAccepted = false;
    let acceptedVersion = null;

    if (doctorSession) {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        isAccepted = Boolean(user.accepted_terms_of_service);
        acceptedVersion = user.terms_of_service_version;
      } catch (_) {}
    } else if (tenantSession) {
      try {
        const tenantInfo = JSON.parse(localStorage.getItem("tenantInfo") || "{}");
        isAccepted = Boolean(tenantInfo.accepted_terms_of_service);
        acceptedVersion = tenantInfo.terms_of_service_version;
      } catch (_) {}
    }

    // Güncel sözleşme onaylı değilse veya eski versiyondaysa modal aç
    const needsConsent = !isAccepted || acceptedVersion !== CURRENT_TERMS_VERSION;
    setOpen(needsConsent);
  }, [location.pathname]);

  useEffect(() => {
    checkConsentStatus();
  }, [checkConsentStatus]);

  // Window storage / focus değiştiğinde de kontrol et
  useEffect(() => {
    const handleStorage = () => checkConsentStatus();
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [checkConsentStatus]);

  const handleAccept = async () => {
    if (!checked) return;
    setSubmitting(true);
    setError(null);

    try {
      const resData = await authService.acceptTermsOfService(CURRENT_TERMS_VERSION);
      
      // Bellekteki doktor verilerini temizle ve yayınla
      clearDoctorCache();
      
      // LocalStorage güncelle
      if (isDoctorSessionActive()) {
        try {
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          user.accepted_terms_of_service = true;
          user.terms_of_service_version = CURRENT_TERMS_VERSION;
          localStorage.setItem("user", JSON.stringify(user));
          if (resData?.user) {
            emitDoctorInfoUpdated(resData.user);
          }
        } catch (_) {}
      } else if (isTenantSessionActive()) {
        try {
          const tenantInfo = JSON.parse(localStorage.getItem("tenantInfo") || "{}");
          tenantInfo.accepted_terms_of_service = true;
          tenantInfo.terms_of_service_version = CURRENT_TERMS_VERSION;
          localStorage.setItem("tenantInfo", JSON.stringify(tenantInfo));
        } catch (_) {}
      }

      setOpen(false);
    } catch (err) {
      console.error("Terms approval failed:", err);
      setError(err?.response?.data?.error || "Sözleşme onaylanırken bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {children}
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={() => {}}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(15, 23, 42, 0.25)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            color: "#ffffff",
            py: 2.5,
            px: 3,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "rgba(16, 185, 129, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#34d399",
            }}
          >
            <GavelOutlinedIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.15rem", lineHeight: 1.3 }}>
              Kullanıcı Sözleşmemiz Güncellendi
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8", display: "block" }}>
              PatientMesh Yasal Bilgilendirme ve Onay Ekranı
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: "#f8fafc" }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
            PatientMesh'i kullanmaya devam edebilmek için güncellenen Kullanıcı Sözleşmesi'ni inceleyip onaylamanız gerekmektedir.
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2, "& .MuiAlert-message": { fontSize: "0.875rem" } }}>
              {error}
            </Alert>
          )}

          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2.5,
              maxHeight: "360px",
              overflowY: "auto",
              bgcolor: "#ffffff",
              borderColor: "#cbd5e1",
              borderRadius: 2,
              fontSize: "0.875rem",
              lineHeight: 1.65,
              color: "#334155",
              "& h1, & h2, & h3, & h4": { color: "#0f172a", mt: 2, mb: 1, fontWeight: 700 },
              "& p": { mb: 1.5 },
              "& ul, & ol": { pl: 2.5, mb: 1.5 },
              "& hr": { my: 2, borderColor: "#e2e8f0" },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => {
                  if (href === "#dpa-modal" || (href && href.includes("dpa"))) {
                    return (
                      <Typography
                        component="span"
                        onClick={(e) => {
                          e.preventDefault();
                          const opened = openDpaInNewTab();
                          if (!opened) {
                            setDpaOpen(true);
                          }
                        }}
                        sx={{
                          color: "#0284C7",
                          fontWeight: 600,
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": { color: "#0369A1" },
                        }}
                      >
                        {children}
                      </Typography>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  );
                },
              }}
            >
              {TERMS_OF_SERVICE_MARKDOWN}
            </ReactMarkdown>
          </Paper>

          <Box sx={{ mt: 2.5, px: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  color="success"
                  sx={{ "&.Mui-checked": { color: "#059669" } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
                  Okudum, anladım ve Kullanıcı Sözleşmesi'ni kabul ediyorum.
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#ffffff",
            borderTop: "1px solid #e2e8f0",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            disabled={!checked || submitting}
            onClick={handleAccept}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <GavelOutlinedIcon />}
            sx={{
              bgcolor: "#059669",
              "&:hover": { bgcolor: "#047857" },
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(5, 150, 105, 0.25)",
            }}
          >
            {submitting ? "Onaylanıyor..." : "Okudum, Kabul Ediyorum"}
          </Button>
        </DialogActions>
      </Dialog>

      <PatientMeshDpaModal open={dpaOpen} onClose={() => setDpaOpen(false)} />
    </>
  );
}
